/**
 * Tracking & attribution ; Layer 5 of the architecture template.
 *
 * - Captures click IDs (fbclid, gclid, msclkid, ttclid, epik) on mount.
 * - Stores first-touch + last-touch UTMs in sessionStorage.
 * - Defers Meta Pixel load until first user interaction OR requestIdleCallback.
 * - Wraps fbq() so we can fire events with a deterministic event_id for
 *   server-side CAPI deduplication.
 *
 * Pixel ID is the same across all 3 routes (single funnel attribution per Q2.a).
 */
export const META_PIXEL_ID = "1756777875289167";

const CLICK_KEYS = [
  "fbclid", "gclid", "msclkid", "ttclid", "epik",
  "clickid", "dxclid",                // DomainEx tracking-link click ID (S2S postback key)
  "ad_id", "adset_id", "campaign_id", // DomainEx recommended ad-URL template
  "hook",                             // ad angle, set per DomainEx link → message match
  "sub1", "sub2", "sub3",             // ad.id / adset.id / campaign.id
  "sub4", "sub5", "sub6",             // ad.name / adset.name / campaign.name
  "sub7", "sub8",                     // placement / site_source_name
];

// Hook values are free text from the URL - only ever used as a lookup key.
const HOOK_RE = /^[a-z0-9_-]{1,64}$/i;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const SS_FIRST = "zenith.first_touch";
const SS_LAST = "zenith.last_touch";
const SS_CLICK = "zenith.click_ids";
const SS_REF = "zenith.referrer";
const SS_LANDING = "zenith.landing_path";

function safeSet(k, v) {
  try {
    sessionStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}
function safeGet(k) {
  try {
    const v = sessionStorage.getItem(k);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

/** Capture attribution from URL on first load. Idempotent ; safe to call many times. */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Click IDs
  const clickIds = safeGet(SS_CLICK) || {};
  let touched = false;
  CLICK_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v && !clickIds[k]) {
      clickIds[k] = v;
      touched = true;
    }
  });
  // A fresh DomainEx click always wins (a new ad click = a new clickid), and
  // the DomainEx pixel's 30-day _dx_cid cookie backfills a return visit that
  // arrives without the param, so the lead still closes the postback loop.
  const freshCid = params.get("clickid") || params.get("dxclid");
  if (freshCid && clickIds.clickid !== freshCid) {
    clickIds.clickid = freshCid;
    touched = true;
  }
  if (!clickIds.clickid) {
    const cookieCid = _readCookie("_dx_cid") || _readCookie("dx_clickid");
    if (cookieCid) {
      clickIds.clickid = cookieCid;
      touched = true;
    }
  }
  if (clickIds.hook && !HOOK_RE.test(clickIds.hook)) {
    delete clickIds.hook;
  }
  // Map DomainEx ad-URL params onto the sub1–sub6 slots the campaign report,
  // Slack and email already group by (sub* from older links still win).
  const utmNow = {};
  UTM_KEYS.forEach((k) => { const v = params.get(k); if (v) utmNow[k] = v; });
  [["sub1", clickIds.ad_id], ["sub2", clickIds.adset_id], ["sub3", clickIds.campaign_id],
   ["sub4", utmNow.utm_content], ["sub6", utmNow.utm_campaign]].forEach(([k, v]) => {
    if (v && !clickIds[k]) {
      clickIds[k] = v;
      touched = true;
    }
  });
  if (touched) safeSet(SS_CLICK, clickIds);

  // UTMs
  const utms = {};
  let hasUtm = false;
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) {
      utms[k] = v;
      hasUtm = true;
    }
  });
  if (hasUtm) {
    if (!safeGet(SS_FIRST)) safeSet(SS_FIRST, utms);
    safeSet(SS_LAST, utms);
  }

  // Referrer / landing path
  if (!safeGet(SS_REF) && document.referrer) safeSet(SS_REF, document.referrer);
  if (!safeGet(SS_LANDING)) safeSet(SS_LANDING, url.pathname + url.search);
}

/** The ad angle this visitor clicked (DomainEx link `hook`), or null. */
export function getHook() {
  const h = (safeGet(SS_CLICK) || {}).hook;
  return h && HOOK_RE.test(h) ? h.toLowerCase() : null;
}

export function getAttribution() {
  return {
    ...(safeGet(SS_CLICK) || {}),
    first_utm: safeGet(SS_FIRST) || null,
    last_utm: safeGet(SS_LAST) || null,
    referrer: safeGet(SS_REF) || null,
    landing_path: safeGet(SS_LANDING) || null,
  };
}

/**
 * Defer-load Meta Pixel. Honors cookie consent ; call only after marketing
 * consent is granted (or implicit by region).
 */
let pixelLoaded = false;
export function loadMetaPixel() {
  if (pixelLoaded || typeof window === "undefined") return;
  if (window.fbq) {
    pixelLoaded = true;
    return;
  }
  pixelLoaded = true;
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
  // PageView is fired once by the event bus (page_view -> PageView with an event_id,
  // mirrored to /api/capi). An id-less PageView here would not dedupe.
}

/** Track an event. `eventId` deduplicates with the server-side CAPI event. */
export function trackEvent(name, props = {}, eventId) {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    if (eventId) window.fbq("track", name, props, { eventID: eventId });
    else window.fbq("track", name, props);
  } catch {
    /* ignore */
  }
}

/**
 * Fire a Meta CUSTOM event (fbq('trackCustom', …)) - used for the V2 mid-funnel
 * signals (Zen_Bridge1_Fit, Zen_Answer, Zen_Exit_*). These are NOT standard
 * events, so they must never go through PIXEL_MAP. Kept separate from trackEvent
 * to make that distinction explicit at every call site.
 */
export function trackCustomEvent(name, props = {}, eventId) {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    if (eventId) window.fbq("trackCustom", name, props, { eventID: eventId });
    else window.fbq("trackCustom", name, props);
  } catch {
    /* ignore */
  }
}

/**
 * Respect a user's "decline non-essential" choice. Meta's pixel keeps PII out
 * of events from this point forward. Call after the user clicks decline.
 */
export function revokeMetaConsent() {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("consent", "revoke");
  } catch {
    /* ignore */
  }
}

/** Re-grant after user opts back in. */
export function grantMetaConsent() {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("consent", "grant");
  } catch {
    /* ignore */
  }
}

/**
 * Load the Meta Pixel immediately on page render.
 *
 * Earlier versions deferred via requestIdleCallback for LCP reasons, but for a
 * Meta-ad funnel the Pixel is mission-critical and Pixel Helper / Test Events
 * need to detect it within the first second of page load. The pixel adds
 * ~30ms blocking ; acceptable for this surface.
 *
 * Honors a prior "decline" by skipping the load entirely.
 */
export function autoLoadMetaPixelOnIdle() {
  if (typeof window === "undefined") return;
  // No consent gate - pixel loads for all visitors.
  loadMetaPixel();
}

export function newEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ---------------------------------------------------------------------------
// Meta attribution cookies - forwarded to server CAPI for match-quality boost
// ---------------------------------------------------------------------------

/**
 * Read a cookie by name from document.cookie.
 * Returns null if not found or running server-side.
 */
function _readCookie(name) {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|;\\s*)" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * Return Meta's attribution cookies for CAPI user_data matching.
 *   _fbc - Facebook click ID (set when visitor arrives via a Meta ad, contains fbclid).
 *          Directly links the CAPI event to the ad click - biggest match-quality boost.
 *   _fbp - Facebook browser ID (set by the Pixel on all visitors).
 *
 * Both are set by Meta's own fbevents.js. Neither contains user PII.
 * Safe to forward to the server for inclusion in CAPI user_data.
 */
export function readFbCookies() {
  return {
    fbc: _readCookie("_fbc") || null,
    fbp: _readCookie("_fbp") || null,
  };
}
